// AI-CONTEXT: See docs/features/participation-value-dashboard-card.md
import { isPostsModel } from './calculateParticipationImpact'
import defaultParticipationParameters from './defaultParticipationParameters'
import currentLadderImpact from './currentLadderImpact'
import attributedValue from '../msiMonetisationHelpers/applyRekenregels'
import { TYPE_LABELS } from './buildParticipationPostsView'

/**
 * @typedef {object} SocietalValueComponent
 * @property {string} key - delta field this component maps to
 * @property {string} label - Dutch label shown in the breakdown
 * @property {number} value - euros, summed across all contributing personas
 * @property {number} attributedValue - Hetzelfde onderdeel toegerekend (MSI-rekenregels), over wie doorgerekend kon worden
 */

/**
 * @typedef {object} SocietalValuePersonaRow
 * @property {string} id - persona ID
 * @property {string} name - persona display name
 * @property {number} total - this persona's contribution to the total (euros)
 * @property {number|null} ladderStepChange - treden moved (t1 - t0), null when unknown
 * @property {number} incomePerson - productivity component (euros)
 * @property {number} volunteerValue - volunteer-work component (euros)
 * @property {number} benefitSavings - benefit-cost saving (euros)
 * @property {number} publicCostSavings - bijstand admin-cost saving (euros)
 * @property {number|null} horizon - Tijdshorizon van de rekenregels, null = onbekend
 * @property {number|null} attributed - Toegerekende waarde van deze deelnemer, null = niet doorgerekend
 * @property {string|null} doseReason - Waarom er geen horizon is
 */

/**
 * @typedef {object} SocietalValueBreakdown
 * @property {'legacy'|'posts'} model - rekenmodel van de vastgezette parameterset
 * @property {number} total - total societal value across personas (euros)
 * @property {Array<SocietalValueComponent>} components - the total split by component
 * @property {Array<SocietalValuePersonaRow>} personas - the total split by persona
 * @property {number} attributedTotal - Toegerekende waarde (rekenregels MSI) van wie doorgerekend kon worden
 * @property {number} notAttributedCount - Deelnemers zonder horizon (duur of uren onbekend)
 * @property {Object<string, number>} notAttributedReasons - Dezelfde telling per reden
 */

const COMPONENTS = [
    { key: 'incomePerson', label: 'Productiviteit' },
    { key: 'volunteerValue', label: 'Waarde vrijwilligerswerk' },
    { key: 'benefitSavings', label: 'Besparing uitkeringskosten' },
    { key: 'publicCostSavings', label: 'Besparing uitvoeringskosten bijstand' },
]

const num = (value) => (Number.isFinite(value) ? value : 0)

// Telt een toegerekend bedrag op, of laat het staan als er niets toe te rekenen is.
const addAttributed = (sum, value) => (value === null || value === undefined ? sum : sum + value)

// Telt toegerekende waarden op; wie niet doorgerekend kon worden telt per reden.
const summariseAttribution = (rows) => {
    const notAttributedReasons = {}
    let attributedTotal = 0
    let notAttributedCount = 0
    rows.forEach((row) => {
        if (row.attributed !== null) {
            attributedTotal += row.attributed
            return
        }
        notAttributedCount += 1
        notAttributedReasons[row.doseReason] = (notAttributedReasons[row.doseReason] || 0) + 1
    })
    return { attributedTotal, notAttributedCount, notAttributedReasons }
}

/**
 * Onder 2026.3 zijn de onderdelen de posten die géén overdracht zijn, in de
 * volgorde van `parameters.posts`: de overdrachten (uitkering, belasting,
 * loonkostensubsidie) vallen over alle stakeholders tegen elkaar weg, dus deze
 * posten tellen samen precies op tot het totaal (de reële waarde, besluit D1).
 *
 * @param {Array<object>} personas
 * @param {object} parameters - parameters 2026.3
 * @param {Object<string, string>|null} birthDates
 */
const buildPostsBreakdown = (personas, parameters, birthDates, orgDefault) => {
    const defs = []
    parameters.posts.filter((post) => !post.overdracht).forEach(({ post, typeWaardering }) => {
        if (!defs.some((d) => d.key === post)) defs.push({ key: post, label: post, typeWaardering })
    })
    const totals = Object.fromEntries(defs.map((d) => [d.key, 0]))
    const attributedTotals = Object.fromEntries(defs.map((d) => [d.key, 0]))
    const typeTotals = Object.fromEntries(Object.keys(TYPE_LABELS).map((t) => [t, 0]))
    const attributedTypeTotals = Object.fromEntries(Object.keys(TYPE_LABELS).map((t) => [t, 0]))
    const personaRows = []

    ;(Array.isArray(personas) ? personas : []).forEach((persona) => {
        const current = currentLadderImpact({ persona, parameters, birthDates, orgDefault })
        if (!current) return
        const { impact, dose } = current
        const values = Object.fromEntries(defs.map((d) => [d.key, 0]))
        const attributedValues = Object.fromEntries(defs.map((d) => [d.key, dose.horizon === null ? null : 0]))
        impact.posts.filter((row) => !row.overdracht).forEach((row) => {
            values[row.post] = (values[row.post] || 0) + row.value
            totals[row.post] = (totals[row.post] || 0) + row.value
            if (row.attributed !== null) {
                attributedValues[row.post] = (attributedValues[row.post] || 0) + row.attributed
                attributedTotals[row.post] = (attributedTotals[row.post] || 0) + row.attributed
            }
        })
        Object.entries(impact.perType).forEach(([type, value]) => {
            typeTotals[type] = (typeTotals[type] || 0) + value
            attributedTypeTotals[type] = addAttributed(attributedTypeTotals[type] || 0, attributedValue(value, dose.horizon))
        })

        personaRows.push({
            id: persona?.ID || '',
            name: persona?.Name || 'Naamloos',
            total: impact.delta.totalSocietalValue,
            ladderStepChange: impact.delta.ladderStepChange,
            values,
            attributedValues,
            horizon: dose.horizon,
            attributed: impact.delta.attributedSocietalValue,
            doseReason: dose.reason,
        })
    })

    const components = defs.map((d) => ({ ...d, value: totals[d.key], attributedValue: attributedTotals[d.key] }))
    return {
        model: 'posts',
        total: components.reduce((sum, c) => sum + c.value, 0),
        components,
        perType: Object.entries(TYPE_LABELS).map(([key, label]) => ({
            key, label, value: typeTotals[key], attributedValue: attributedTypeTotals[key],
        })),
        personas: personaRows,
        ...summariseAttribution(personaRows),
    }
}

/**
 * Builds the full construction of the dashboard "Maatschappelijke waarde" total:
 * the grand total, the total split per component, and the total split per
 * persona. Mirrors sumCurrentSocietalValue's gate — only personas whose current
 * situation (t1) is filled contribute, so an empty current situation never shows
 * a phantom saving.
 *
 * @param {Array<{ID?: string, Name?: string, Participation?: {t0?: object, t1?: object}}>} personas
 * @param {object} [parameters] - participation model parameters
 * @param {Object<string, string>|null} [birthDates] - PersonaID → geboortedatum, voor de leeftijdsovergang van 2026.3
 * @param {{ Months?: *, HoursPerWeek?: * }|null} [orgDefault] - CompagnyMeta.DefaultInterventionDose (rekenregels MSI)
 * @returns {SocietalValueBreakdown}
 */
const buildSocietalValueBreakdown = (personas, parameters = defaultParticipationParameters, birthDates = null, orgDefault = null) => {
    if (isPostsModel(parameters)) return buildPostsBreakdown(personas, parameters, birthDates, orgDefault)

    const componentTotals = { incomePerson: 0, volunteerValue: 0, benefitSavings: 0, publicCostSavings: 0 }
    const attributedComponentTotals = { incomePerson: 0, volunteerValue: 0, benefitSavings: 0, publicCostSavings: 0 }
    const personaRows = []

    if (Array.isArray(personas)) {
        personas.forEach((persona) => {
            const current = currentLadderImpact({ persona, parameters, birthDates, orgDefault })
            if (!current) return
            const { impact: { delta }, dose } = current

            const incomePerson = num(delta.incomePerson)
            const volunteerValue = num(delta.volunteerValue)
            const benefitSavings = num(delta.benefitCost)
            const publicCostSavings = num(delta.publicCost)

            componentTotals.incomePerson += incomePerson
            componentTotals.volunteerValue += volunteerValue
            componentTotals.benefitSavings += benefitSavings
            componentTotals.publicCostSavings += publicCostSavings
            const attributedValues = {
                incomePerson: attributedValue(incomePerson, dose.horizon),
                volunteerValue: attributedValue(volunteerValue, dose.horizon),
                benefitSavings: attributedValue(benefitSavings, dose.horizon),
                publicCostSavings: attributedValue(publicCostSavings, dose.horizon),
            }
            Object.keys(attributedComponentTotals).forEach((key) => {
                attributedComponentTotals[key] = addAttributed(attributedComponentTotals[key], attributedValues[key])
            })

            personaRows.push({
                id: persona?.ID || '',
                name: persona?.Name || 'Naamloos',
                total: incomePerson + volunteerValue + benefitSavings + publicCostSavings,
                ladderStepChange: delta.ladderStepChange,
                incomePerson,
                volunteerValue,
                benefitSavings,
                publicCostSavings,
                attributedValues,
                horizon: dose.horizon,
                attributed: delta.attributedSocietalValue,
                doseReason: dose.reason,
            })
        })
    }

    const components = COMPONENTS.map(({ key, label }) => ({
        key, label, value: componentTotals[key], attributedValue: attributedComponentTotals[key],
    }))
    const total = components.reduce((sum, component) => sum + component.value, 0)

    return { model: 'legacy', total, components, personas: personaRows, ...summariseAttribution(personaRows) }
}

export default buildSocietalValueBreakdown
